"use client";

import type { Transition } from "motion/react";
import {
  Children,
  isValidElement,
  type ReactNode,
  useMemo,
  useRef,
} from "react";
import useMeasure from "react-use-measure";
import { cn } from "@/lib/utils";
import { DEFAULT_CHART_ENTER_TRANSITION } from "./animation";
import {
  defaultScatterColors,
  type LineConfig,
  type Margin,
} from "./chart-context";
import type { ChartPhase } from "./chart-phase";
import type { ProjectionMode } from "./projection";
import { Scatter, type ScatterProps } from "./scatter";
import { ScatterChartInner } from "./scatter-chart-shell";
import type { ScatterProjectionLayout } from "./scatter-projection-context";
import type { ScatterInteractionMode } from "./use-scatter-chart-interaction";

export interface ScatterChartProps {
  /** Data array — each item should have a date field and numeric values */
  data: Record<string, unknown>[];
  /** Key in data for the x-axis (date). Default: "date" */
  xDataKey?: string;
  /**
   * X-scale kind: "time" (Date-domain, default), "linear" (numeric), or "band" (string).
   */
  xScaleKind?: import("./x-scale").XScaleKind;
  /** Chart margins */
  margin?: Partial<Margin>;
  /** Animation duration in milliseconds. Default: 1100 */
  animationDuration?: number;
  /** CSS easing for clip-reveal. Default: cubic-bezier(0.85, 0, 0.15, 1) */
  animationEasing?: string;
  enterTransition?: Transition;
  revealSignature?: string;
  /** Aspect ratio as "width / height". Default: "2 / 1" */
  aspectRatio?: string;
  /** Additional class name for the container */
  className?: string;
  /** Child components (Scatter, Grid, ChartTooltip, XAxis, etc.) */
  children: ReactNode;
  onPhaseChange?: (phase: ChartPhase) => void;
  projection?: ProjectionMode;
  projectionDepth?: number;
  projectionLayout?: ScatterProjectionLayout;
  interaction?: ScatterInteractionMode;
  xDomain?: [number, number];
  yDomain?: [number, number];
}

const DEFAULT_MARGIN: Margin = { top: 40, right: 40, bottom: 40, left: 40 };

function extractScatterConfigs(children: ReactNode): LineConfig[] {
  const configs: LineConfig[] = [];
  let seriesIndex = 0;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    const childType = child.type as {
      displayName?: string;
      name?: string;
    };
    const componentName =
      typeof child.type === "function"
        ? childType.displayName || childType.name || ""
        : "";

    const props = child.props as ScatterProps | undefined;
    const isScatterComponent =
      componentName === "Scatter" ||
      child.type === Scatter ||
      (props && typeof props.dataKey === "string" && props.dataKey.length > 0);

    if (isScatterComponent && props?.dataKey) {
      const seriesColor =
        defaultScatterColors[seriesIndex % defaultScatterColors.length] ??
        defaultScatterColors[0];
      configs.push({
        dataKey: props.dataKey,
        stroke: props.fill || props.stroke || seriesColor,
        strokeWidth: props.radius ?? 5,
        yAxisId: props.yAxisId,
      });
      seriesIndex += 1;
    }
  });

  return configs;
}

interface ChartInnerProps {
  width: number;
  height: number;
  data: Record<string, unknown>[];
  xDataKey: string;
  xScaleKind?: import("./x-scale").XScaleKind;
  margin: Margin;
  animationDuration: number;
  animationEasing?: string;
  enterTransition?: Transition;
  revealSignature?: string;
  children: ReactNode;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onPhaseChange?: (phase: ChartPhase) => void;
  projection?: ProjectionMode;
  projectionDepth?: number;
  projectionLayout?: ScatterProjectionLayout;
  interaction?: ScatterInteractionMode;
  xDomain?: [number, number];
  yDomain?: [number, number];
}

function ChartInner({
  width,
  height,
  data,
  xDataKey,
  xScaleKind,
  margin,
  animationDuration,
  animationEasing,
  enterTransition,
  revealSignature,
  children,
  containerRef,
  onPhaseChange,
  projection,
  projectionDepth,
  projectionLayout,
  interaction,
  xDomain,
  yDomain,
}: ChartInnerProps) {
  const lines = useMemo(() => extractScatterConfigs(children), [children]);

  return (
    <ScatterChartInner
      animationDuration={animationDuration}
      animationEasing={animationEasing}
      containerRef={containerRef}
      data={data}
      enterTransition={enterTransition}
      height={height}
      interaction={interaction}
      lines={lines}
      margin={margin}
      onPhaseChange={onPhaseChange}
      projection={projection}
      projectionDepth={projectionDepth}
      projectionLayout={projectionLayout}
      revealSignature={revealSignature}
      width={width}
      xDataKey={xDataKey}
      xDomain={xDomain}
      xScaleKind={xScaleKind}
      yDomain={yDomain}
    >
      {children}
    </ScatterChartInner>
  );
}

export function ScatterChart({
  data,
  xDataKey = "date",
  xScaleKind,
  margin: marginProp,
  animationDuration = 1100,
  animationEasing,
  enterTransition = DEFAULT_CHART_ENTER_TRANSITION,
  revealSignature,
  aspectRatio = "2 / 1",
  className = "",
  children,
  onPhaseChange,
  projection,
  projectionDepth,
  projectionLayout,
  interaction,
  xDomain,
  yDomain,
}: ScatterChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const margin = { ...DEFAULT_MARGIN, ...marginProp };
  const [measureRef, bounds] = useMeasure({ debounce: 10 });

  const setContainerRef = (node: HTMLDivElement | null) => {
    containerRef.current = node;
    measureRef(node);
  };

  const width = bounds.width ?? 0;
  const height = bounds.height ?? 0;

  return (
    <div
      className={cn("relative w-full", className)}
      ref={setContainerRef}
      style={{ aspectRatio, touchAction: "manipulation" }}
    >
      {width > 0 && height > 0 ? (
        <ChartInner
          animationDuration={animationDuration}
          animationEasing={animationEasing}
          containerRef={containerRef}
          data={data}
          enterTransition={enterTransition}
          height={height}
          interaction={interaction}
          margin={margin}
          onPhaseChange={onPhaseChange}
          projection={projection}
          projectionDepth={projectionDepth}
          projectionLayout={projectionLayout}
          revealSignature={revealSignature}
          width={width}
          xDataKey={xDataKey}
          xDomain={xDomain}
          xScaleKind={xScaleKind}
          yDomain={yDomain}
        >
          {children}
        </ChartInner>
      ) : null}
    </div>
  );
}

ScatterChart.displayName = "ScatterChart";

export { Scatter, type ScatterProps } from "./scatter";

export default ScatterChart;
